import { inject, Injectable } from '@angular/core';
import { isIndividual } from '@rma/generic/domain/helpers';
import { ProfileType, VerticalType } from '@rma/generic/domain/types';
import {
  ProfileContextProjectionService,
  ProfileIdentifier,
  ProfileIdentifierWithVertical,
  ProfileUserType,
  ProfileUserTypeWithVertical,
} from '@rma/generic/ng/user';
import { cache } from 'decorator-cache-getter';
import { map, Observable } from 'rxjs';
import { CurrentDashboardProfileContext } from '../../entities/dashboard-profile-user-context.models';

/**
 * DashboardProfileContextService
 *
 * Dashboard-facing facade over the shared {@link ProfileContextProjectionService}. The common streams delegate to the
 * kernel projection; only the dashboard-specific deltas (e.g. isAssistant$) live here.
 */
@Injectable({
  providedIn: 'root',
})
export class DashboardProfileContextService {
  private readonly projection = inject(ProfileContextProjectionService);

  public get profileContextChanged$(): Observable<string> {
    return this.projection.profileContextChanged$;
  }

  public get nullableProfileContext$(): Observable<CurrentDashboardProfileContext | null> {
    return this.projection.nullableProfileContext$;
  }

  public get profileContext$(): Observable<CurrentDashboardProfileContext> {
    return this.projection.profileContext$;
  }

  public get profileCode$(): Observable<string> {
    return this.projection.profileCode$;
  }

  public get profileEmail$(): Observable<string> {
    return this.projection.profileEmail$;
  }

  public get profileImage$(): Observable<string> {
    return this.projection.profileImage$;
  }

  public get profileType$(): Observable<ProfileType> {
    return this.projection.profileType$;
  }

  public get primaryVerticalType$(): Observable<VerticalType> {
    return this.projection.primaryVerticalType$;
  }

  public get isFinance$(): Observable<boolean> {
    return this.projection.isFinance$;
  }

  public get isNotFinance$(): Observable<boolean> {
    return this.projection.isNotFinance$;
  }

  public get isSales$(): Observable<boolean> {
    return this.projection.isSales$;
  }

  public get profileIdentifier$(): Observable<ProfileIdentifier> {
    return this.projection.profileIdentifier$;
  }

  public get profileIdentifierWithVertical$(): Observable<ProfileIdentifierWithVertical> {
    return this.projection.profileIdentifierWithVertical$;
  }

  public get nullableProfileIdentifier$(): Observable<ProfileIdentifier | null> {
    return this.projection.nullableProfileIdentifier$;
  }

  /**
   * @deprecated Use profileType$ instead.
   */
  public get profileUserType$(): Observable<ProfileUserType> {
    return this.projection.profileUserType$;
  }

  /**
   * @deprecated Use a combination of primaryVerticalType and profileType.
   */
  public get profileVerticalUserType$(): Observable<ProfileUserTypeWithVertical> {
    return this.projection.profileVerticalUserType$;
  }

  // @deprecated are you sure you don't mean profileIdentifier$ instead?
  public get profileUserTypeAndProfileCode$(): Observable<{ userType: ProfileUserType; profileCode: string }> {
    return this.projection.profileUserTypeAndProfileCode$;
  }

  public get isIndividual$(): Observable<boolean> {
    return this.projection.isIndividual$;
  }

  public get isAgency$(): Observable<boolean> {
    return this.projection.isAgency$;
  }

  public get isTeam$(): Observable<boolean> {
    return this.projection.isTeam$;
  }

  public get isOffice$(): Observable<boolean> {
    return this.projection.isOffice$;
  }

  public get isAgent$(): Observable<boolean> {
    return this.projection.isAgent$;
  }

  public get isMortgageBroker$(): Observable<boolean> {
    return this.projection.isMortgageBroker$;
  }

  @cache
  public get isAssistant$(): Observable<boolean> {
    return this.projection.profileContext$.pipe(map(({ profileType, isOwnProfile }) => isIndividual(profileType) && !isOwnProfile));
  }

  public get profileUrl$(): Observable<string> {
    return this.projection.profileUrl$;
  }

  public get currentAgencyContextCode$(): Observable<string | undefined> {
    return this.projection.currentAgencyContextCode$;
  }

  public get shortName$(): Observable<string> {
    return this.projection.shortName$;
  }

  public get parentName$(): Observable<string> {
    return this.projection.parentName$;
  }

  public refreshProfileContext(): void {
    this.projection.refreshProfileContext();
  }

  public setProfileContext(profileType: ProfileType, profileCode: string): void {
    this.projection.setProfileContext(profileType, profileCode);
  }
}
